import { Tooltip } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Icon, notification } from 'ui';
import moment from 'utils/cops-moment';
import Table from '../../../common/table/table';
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import { deleteCampaign, fetchCampaigns } from '../../containers/campaigns/campaigns-actions';
import EditCampaignSidepanel from './edit-campaign-sidepanel';

const Container = styled.div`
  margin: 25px 15px;
`;

const AddButton = styled(Button)`
  margin: 20px 0;
  text-transform: uppercase;
`;

const Icons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 20px;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.disabled ? 'rgba(0, 0, 0, 0.3)' : p.theme.colors.theme};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const Campaigns = ({
  intl,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isLoadingCampaigns,
    campaignsError,
    campaigns,
    isUpdatingCampaign,
    updateCampaignSuccess,
    frequencies,
  } = useSelector(state => state.campaigns);
  const [showConfirm] = useConfirmationMessage();

  useEffect(() => {
    dispatch(fetchCampaigns());
  }, []);

  useEffect(() => {
    if (updateCampaignSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'message_center.SUCCESS' }),
        placement: 'top',
      });
    }
  }, [updateCampaignSuccess]);

  const columns = [
    {
      id: 'title',
      fieldName: 'title',
      displayName: intl.formatMessage({ id: 'campaigns.TITLE' }),
      width: '300px',
      tooltip: row => row.title,
    },
    {
      id: 'startDate',
      displayName: intl.formatMessage({ id: 'campaigns.STARTS' }),
      renderFn: row => row?.calculationInfo?.startDate ?
        moment(row?.calculationInfo?.startDate).format('L') :
        null,
      width: '200px',
    },
    {
      id: 'frequency',
      displayName: intl.formatMessage({ id: 'campaigns.FREQUENCY' }),
      renderFn: row => frequencies?.find(f => f.id === row.calculationInfo?.frequency)?.name,
      width: '150px',
    },
    {
      id: 'endDate',
      displayName: intl.formatMessage({ id: 'campaigns.ENDS' }),
      renderFn: row => row?.calculationInfo?.endDate ?
        moment(row?.calculationInfo?.endDate).format('L') :
        null,
      width: '200px',
    },
    {
      id: 'scopes',
      displayName: intl.formatMessage({ id: 'campaigns.CUSTOMER_CENTERS' }),
      renderFn: row => row.scopes?.map(s => s.name).join(', '),
      width: '300px',
    },
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '60px',
      renderFn: row => <Icons>
        <Tooltip
          title={intl.formatMessage({ id: 'campaigns.EDIT_CAMPAIGN' })}
          key='edit'
          mouseEnterDelay={0.5}
          getPopupContainer={() => document.body}
        >
          <div>
            <StyledIcon
              type='pen'
              onClick={!isUpdatingCampaign ? () => navigate(`campaign/${row.id}`) : null}
              disabled={isUpdatingCampaign}
            />
          </div>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({ id: 'campaigns.DELETE_CAMPAIGN' })}
          key='delete'
          mouseEnterDelay={0.5}
          getPopupContainer={() => document.body}
        >
          <div>
            <StyledIcon
              type='trash-can'
              onClick={isUpdatingCampaign ?
                null :
                () => showConfirm(() => dispatch(deleteCampaign({ id: row.id })))}
              disabled={isUpdatingCampaign}
            />
          </div>
        </Tooltip>
      </Icons>,
    },
  ];

  return (
    <Container>
      <ErrorHandler type='notification' error={campaignsError} />
      <Table
        columns={columns}
        data={campaigns || []}
        labelInColumn={true}
        isLoadingData={isLoadingCampaigns}
      />
      <AddButton
        type='secondary'
        onClick={() => navigate('campaign')}
        disabled={isLoadingCampaigns && !campaigns?.length}
        data-test-id='btn-add-campaign'
      >
        {intl.formatMessage({ id: 'campaigns.ADD_CAMPAIGN' })}
      </AddButton>
      <Routes>
        <Route
          path={'campaign/:id?'}
          element={<EditCampaignSidepanel />}
        />
      </Routes>
    </Container>
  );
};

Campaigns.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(Campaigns);
